import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  Menu,
  X,
  TestTubes,
  UserIcon,
  BookOpen,
  GraduationCap,
  Target,
  ArrowRight,
  ChevronRight,
  LogIn,
} from "lucide-react";

// ── Course data ────────────────────────────────────────────────────
const FOUNDATION_COURSES = [
  {
    label: "Class 8 Foundation",
    desc: "Build strong concepts & basics",
    badge: "8th",
    badgeColor: "bg-blue-100 text-blue-700",
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    to: "/courses/class-8-foundation",
  },
  {
    label: "Class 9 Foundation",
    desc: "Advance with conceptual depth",
    badge: "9th",
    badgeColor: "bg-emerald-100 text-emerald-700",
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    to: "/courses/class-9-foundation",
  },
  {
    label: "Class 10 Board Prep",
    desc: "Ace CBSE board examinations",
    badge: "10th",
    badgeColor: "bg-amber-100 text-amber-700",
    iconBg: "bg-amber-50",
    iconColor: "text-amber-600",
    to: "/courses/class-10-board-preparation",
  },
];

const SENIOR_COURSES = [
  {
    label: "Class 11 Foundation",
    desc: "Science, Commerce & Arts streams",
    badge: "11th",
    badgeColor: "bg-violet-100 text-violet-700",
    iconBg: "bg-violet-50",
    iconColor: "text-violet-600",
    to: "/courses/class-11-foundation",
  },
  {
    label: "Class 12 Board Prep",
    desc: "Excel in final board exams",
    badge: "12th",
    badgeColor: "bg-rose-100 text-rose-700",
    iconBg: "bg-rose-50",
    iconColor: "text-rose-600",
    to: "/courses/class-12-board-preparation",
  },
  {
    label: "Computer Science",
    desc: "Coding, IT & digital skills",
    badge: "CS",
    badgeColor: "bg-teal-100 text-teal-700",
    iconBg: "bg-teal-50",
    iconColor: "text-teal-600",
    to: "/courses/computer-science",
    isSpecial: true,
  },
];

// ── Helper: scroll to top on navigate ─────────────────────────────
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

// ── Course card for mega menu ──────────────────────────────────────
function CourseCard({ course, onClick }) {
  return (
    <Link
      to={course.to}
      onClick={onClick}
      className="group flex items-center gap-3 p-3 rounded-xl hover:bg-[#0097b2]/5 transition-all duration-200 border border-transparent hover:border-[#0097b2]/20"
    >
      <div
        className={`flex-shrink-0 w-10 h-10 ${course.iconBg} rounded-lg flex items-center justify-center`}
      >
        <span className={`text-sm font-bold ${course.iconColor}`}>
          {course.badge}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-800 group-hover:text-[#0097b2] transition-colors">
            {course.label}
          </span>
          {course.isSpecial && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full">
              HOT
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 truncate">{course.desc}</p>
      </div>
      <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-[#0097b2] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
    </Link>
  );
}

// ── Main Navbar ────────────────────────────────────────────────────
export default function Navbar() {
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCoursesOpen, setMobileCoursesOpen] = useState(false);
  const [mobileFoundationOpen, setMobileFoundationOpen] = useState(false);
  const [mobileSeniorOpen, setMobileSeniorOpen] = useState(false);

  const megaRef = useRef(null);
  const timeoutRef = useRef(null);

  // close mega menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (megaRef.current && !megaRef.current.contains(e.target)) {
        setMegaOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openMega = () => {
    clearTimeout(timeoutRef.current);
    setMegaOpen(true);
  };

  const closeMega = () => {
    timeoutRef.current = setTimeout(() => setMegaOpen(false), 120);
  };

  const closeMegaImmediately = () => {
    clearTimeout(timeoutRef.current);
    setMegaOpen(false);
  };

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileCoursesOpen(false);
    setMobileFoundationOpen(false);
    setMobileSeniorOpen(false);
  };

  const openAdmissionEnquiry = () => {
    const phoneNumber = "919918519991";
    const message =
      "Hello SSS Achievers, I would like to enquire about admissions. Please share complete details. Thank you.";
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const navLinkClass =
    "text-gray-700 hover:text-[#0097b2] text-sm font-medium transition-colors duration-200 whitespace-nowrap";

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link
            to="/"
            onClick={scrollTop}
            className="flex-shrink-0"
            aria-label="SSS Achievers Home"
          >
            <img
              src="/final logo new.png"
              alt="SSS Achievers – Best Coaching in Ghazipur"
              width={145}
              height={58}
              className="h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6 ml-8">
            <Link to="/" onClick={scrollTop} className={navLinkClass}>
              Home
            </Link>

            {/* Mega Menu trigger */}
            <div
              ref={megaRef}
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
            >
              <button
                className={`${navLinkClass} flex items-center gap-1 focus:outline-none`}
                aria-haspopup="true"
                aria-expanded={megaOpen}
                onClick={() => setMegaOpen((p) => !p)}
              >
                <BookOpen className="w-4 h-4" />
                Courses
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* ── Mega Drop ── */}
              {megaOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[640px] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
                  onMouseEnter={openMega}
                  onMouseLeave={closeMega}
                  role="menu"
                >
                  {/* Header strip */}
                  <div className="bg-gradient-to-r from-[#0097b2]/10 to-[#fcbf0a]/10 px-6 py-3 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Our Courses
                    </span>
                    <Link
                      to="/courses"
                      onClick={() => { scrollTop(); closeMegaImmediately(); }}
                      className="text-xs text-[#0097b2] font-semibold hover:underline flex items-center gap-1"
                    >
                      View All <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>

                  <div className="grid grid-cols-2 gap-0">
                    {/* Column 1 – Foundation */}
                    <div className="p-4 border-r border-gray-100">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <GraduationCap className="w-4 h-4 text-[#0097b2]" />
                        <span className="text-xs font-bold text-[#0097b2] uppercase tracking-wider">
                          Foundation (8th – 10th)
                        </span>
                      </div>
                      <div className="space-y-1">
                        {FOUNDATION_COURSES.map((c) => (
                          <CourseCard
                            key={c.to}
                            course={c}
                            onClick={() => { scrollTop(); closeMegaImmediately(); }}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Column 2 – Senior */}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3 px-1">
                        <Target className="w-4 h-4 text-[#fcbf0a]" />
                        <span className="text-xs font-bold text-amber-500 uppercase tracking-wider">
                          Senior & Computer (11th – 12th)
                        </span>
                      </div>
                      <div className="space-y-1">
                        {SENIOR_COURSES.map((c) => (
                          <CourseCard
                            key={c.to}
                            course={c}
                            onClick={() => { scrollTop(); closeMegaImmediately(); }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Bottom CTA strip */}
                  <div className="bg-gradient-to-r from-[#0097b2] to-[#007a91] px-6 py-3 flex items-center justify-between">
                    <span className="text-white text-sm font-medium">
                      Not sure which course to pick?
                    </span>
                    <button
                      onClick={() => { openAdmissionEnquiry(); closeMegaImmediately(); }}
                      className="text-xs bg-white text-[#0097b2] font-bold px-4 py-1.5 rounded hover:bg-[#fcbf0a] hover:text-white transition-colors duration-200"
                    >
                      Get Free Counselling
                    </button>
                  </div>
                </div>
              )}
            </div>

            <Link to="/about" onClick={scrollTop} className={navLinkClass}>
              About Us
            </Link>
            <Link to="/contact" onClick={scrollTop} className={navLinkClass}>
              Contact Us
            </Link>
            <a
              href="https://testseries.sssachievers.com/"
              target="_blank"
              rel="noopener noreferrer"
              className={`${navLinkClass} flex items-center gap-1`}
            >
              <TestTubes className="w-4 h-4" />
              Test Series
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3 ml-6">
            <a
              href="https://student.sssachievers.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 border border-[#0097b2] text-[#0097b2] text-sm font-semibold rounded hover:bg-[#0097b2] hover:text-white transition-colors duration-200 whitespace-nowrap"
            >
              <LogIn className="w-4 h-4" />
              Student Login
            </a>
            <button
              onClick={openAdmissionEnquiry}
              className="flex items-center gap-1.5 px-4 py-2 bg-[#0097b2] text-white text-sm font-semibold rounded hover:bg-[#007a91] transition-colors duration-200 shadow-sm whitespace-nowrap"
            >
              <UserIcon className="w-4 h-4" />
              Enquiry Now
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-gray-700 rounded hover:bg-gray-100 transition-colors"
            onClick={() => setMobileOpen((p) => !p)}
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* ── Mobile Menu ── */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-3 space-y-1">
            {/* Home */}
            <Link
              to="/"
              onClick={() => { scrollTop(); closeMobile(); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-[#0097b2]/5 hover:text-[#0097b2] font-medium text-sm transition-colors"
            >
              Home
            </Link>

            {/* Courses accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-[#0097b2]/5 font-medium text-sm transition-colors"
                onClick={() => setMobileCoursesOpen((p) => !p)}
              >
                <span className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  Courses
                </span>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${mobileCoursesOpen ? "rotate-180" : ""}`}
                />
              </button>

              {mobileCoursesOpen && (
                <div className="ml-3 mt-1 border-l-2 border-[#0097b2]/20 pl-3 space-y-1">
                  {/* Foundation sub-section */}
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-[#0097b2] hover:bg-[#0097b2]/5 transition-colors"
                    onClick={() => setMobileFoundationOpen((p) => !p)}
                  >
                    <span className="flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" />
                      Foundation (8th – 10th)
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${mobileFoundationOpen ? "rotate-90" : ""}`}
                    />
                  </button>

                  {mobileFoundationOpen && (
                    <div className="ml-3 space-y-1">
                      {FOUNDATION_COURSES.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => { scrollTop(); closeMobile(); }}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#0097b2]/5 transition-colors group"
                        >
                          <span
                            className={`w-8 h-8 ${c.iconBg} rounded-md flex items-center justify-center text-xs font-bold ${c.iconColor} flex-shrink-0`}
                          >
                            {c.badge}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-slate-800 group-hover:text-[#0097b2]">
                              {c.label}
                            </div>
                            <div className="text-xs text-slate-500">{c.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* Senior sub-section */}
                  <button
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-semibold text-amber-600 hover:bg-amber-50 transition-colors"
                    onClick={() => setMobileSeniorOpen((p) => !p)}
                  >
                    <span className="flex items-center gap-2">
                      <Target className="w-4 h-4" />
                      Senior & Competitive (11th – 12th)
                    </span>
                    <ChevronRight
                      className={`w-4 h-4 transition-transform ${mobileSeniorOpen ? "rotate-90" : ""}`}
                    />
                  </button>

                  {mobileSeniorOpen && (
                    <div className="ml-3 space-y-1">
                      {SENIOR_COURSES.map((c) => (
                        <Link
                          key={c.to}
                          to={c.to}
                          onClick={() => { scrollTop(); closeMobile(); }}
                          className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[#0097b2]/5 transition-colors group"
                        >
                          <span
                            className={`w-8 h-8 ${c.iconBg} rounded-md flex items-center justify-center text-xs font-bold ${c.iconColor} flex-shrink-0`}
                          >
                            {c.badge}
                          </span>
                          <div>
                            <div className="text-sm font-medium text-slate-800 group-hover:text-[#0097b2] flex items-center gap-1">
                              {c.label}
                              {c.isSpecial && (
                                <span className="text-[9px] font-bold px-1 py-0.5 bg-teal-100 text-teal-700 rounded-full">
                                  HOT
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">{c.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}

                  {/* View all */}
                  <Link
                    to="/courses"
                    onClick={() => { scrollTop(); closeMobile(); }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-[#0097b2] font-semibold hover:underline"
                  >
                    View All Courses <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/about"
              onClick={() => { scrollTop(); closeMobile(); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-[#0097b2]/5 hover:text-[#0097b2] font-medium text-sm transition-colors"
            >
              About Us
            </Link>
            <Link
              to="/contact"
              onClick={() => { scrollTop(); closeMobile(); }}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-[#0097b2]/5 hover:text-[#0097b2] font-medium text-sm transition-colors"
            >
              Contact Us
            </Link>
            <a
              href="https://testseries.sssachievers.com/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMobile}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-gray-700 hover:bg-[#0097b2]/5 hover:text-[#0097b2] font-medium text-sm transition-colors"
            >
              <TestTubes className="w-4 h-4" />
              Test Series
            </a>

            {/* Mobile CTAs */}
            <div className="pt-2 pb-1 space-y-2">
              <a
                href="https://student.sssachievers.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobile}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#0097b2] text-[#0097b2] text-sm font-semibold rounded hover:bg-[#0097b2] hover:text-white transition-colors"
              >
                <LogIn className="w-4 h-4" />
                Student Login
              </a>
              <button
                onClick={() => { openAdmissionEnquiry(); closeMobile(); }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0097b2] text-white text-sm font-semibold rounded hover:bg-[#007a91] transition-colors"
              >
                <UserIcon className="w-4 h-4" />
                Enquiry Now on WhatsApp
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
